import { Box, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IBodyShellImageSlot } from "../../../../services/bodyshellService";
import {
  BodyShellImageFileNames,
  BodyShellImageValues,
} from "../StepImages/StepImages";
import { containerSx } from "./StepVisualization.styles";

interface IStepVisualization {
  values: BodyShellImageValues;
  fileNames: BodyShellImageFileNames;
}

interface VisualizationItem {
  labelKey: string;
  slot: IBodyShellImageSlot;
}

interface VisualizationSection {
  titleKey: string;
  items: VisualizationItem[];
}

const visualizationSections: VisualizationSection[] = [
  {
    titleKey: "car",
    items: [
      {
        labelKey: "car",
        slot: "car",
      },
    ],
  },
  {
    titleKey: "frontDoors",
    items: [
      {
        labelKey: "frontLeftC",
        slot: "frontLeftC",
      },
      {
        labelKey: "frontLeft",
        slot: "frontLeft",
      },
      {
        labelKey: "frontRightC",
        slot: "frontRightC",
      },
      {
        labelKey: "frontRight",
        slot: "frontRight",
      },
    ],
  },
  {
    titleKey: "rearDoors",
    items: [
      {
        labelKey: "rearLeftC",
        slot: "rearLeftC",
      },
      {
        labelKey: "rearLeft",
        slot: "rearLeft",
      },
      {
        labelKey: "rearRightC",
        slot: "rearRightC",
      },
      {
        labelKey: "rearRight",
        slot: "rearRight",
      },
    ],
  },
  {
    titleKey: "windows",
    items: [
      {
        labelKey: "frontLeftCOw",
        slot: "frontLeftCOw",
      },
      {
        labelKey: "frontRightCOw",
        slot: "frontRightCOw",
      },
      {
        labelKey: "rearLeftCOw",
        slot: "rearLeftCOw",
      },
      {
        labelKey: "rearRightCOw",
        slot: "rearRightCOw",
      },
    ],
  },
  {
    titleKey: "trunkHood",
    items: [
      {
        labelKey: "trunk",
        slot: "trunk",
      },
      {
        labelKey: "hood",
        slot: "hood",
      },
    ],
  },
  {
    titleKey: "sunRoof",
    items: [
      {
        labelKey: "sunRoof",
        slot: "sunRoof",
      },
      {
        labelKey: "sunRoofC",
        slot: "sunRoofC",
      },
    ],
  },
];

function getImageSrc(image?: string) {
  if (!image) {
    return undefined;
  }

  if (
    image.startsWith("data:") ||
    image.startsWith("http://") ||
    image.startsWith("https://")
  ) {
    return image;
  }

  return `data:image/png;base64,${image}`;
}

export default function StepVisualization({
  values,
  fileNames,
}: IStepVisualization) {
  const { t } = useTranslation();

  return (
    <Box sx={containerSx}>
      <Typography
        sx={{
          fontSize: "16px",
          marginBottom: 3,
        }}
      >
        {t("bodyshell.step2.reviewInstruction")}
      </Typography>

      {visualizationSections.map((section, sectionIndex) => (
        <Box
          key={section.titleKey}
          sx={{
            marginBottom: 3,
            paddingBottom: 3,
            borderBottom: "1px solid var(--gray-200)",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              marginBottom: 1.5,
            }}
          >
            {sectionIndex + 1}.{" "}
            {t(`bodyshell.step2.groups.${section.titleKey}`)}
          </Typography>

          <Grid container spacing={2}>
            {section.items.map((item) => {
              const image = values[item.slot];
              const imageSrc = getImageSrc(image);
              const fileName = fileNames[item.slot];
              const label = t(`bodyshell.step2.fields.${item.labelKey}`);

              return (
                <Grid
                  key={item.slot}
                  size={section.items.length === 1 ? 12 : 3}
                >
                  <Typography
                    sx={{
                      fontSize: "16px",
                      marginBottom: image ? 2 : 0,
                    }}
                  >
                    {label}
                    {section.items.length > 1 && ":"}
                  </Typography>

                  {image && (
                    <Box
                      sx={{
                        width: "100%",
                        height: 60,
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                          section.items.length === 1 ? "flex-start" : "center",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        component="img"
                        src={imageSrc}
                        alt={label}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  )}

                  {fileName && (
                    <Typography
                      sx={{
                        display: "block",
                        fontSize: "16px",
                        color: "var(--gray-300)",
                        marginBottom: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fileName}
                    </Typography>
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}

      <Typography
        sx={{
          fontSize: "16px",
          color: "var(--gray-600)",
          marginTop: 1,
        }}
      >
        {t("bodyshell.step2.requiredNote")}
      </Typography>
    </Box>
  );
}
